import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/app/store';

import currencyExchangeServices from '../../api/currencyExchangeService';

export interface Country {
  id: string;
  code: string;
}

export interface Recipient {
  fullName: string;
  bankName: string;
  bankCode: string;
  swiftCode: string;
  accountNumber: string;
  iban: string;
  address: string;
  town: string;
  postalCode: string;
  country: Country;
}

export interface SourceAccount {
  iban: string;
  currency: string;
}

export interface User {
  id: string;
  username: string;
  fullName: string;
}

export interface CurrencyExchange {
  id: string;
  requestId: string;
  sourceAccount: SourceAccount;
  recipient: Recipient;
  sourceAmount: number;
  estimatedAmount: number;
  fee: number;
  exchangeAmount: number;
  exchangeRateApplied: number;
  exchangeCurrency: string;
  requestStatus: string;
  requestType: string;
  userApprovalRequired: boolean;
  rejectionReason: string;
  user: User;
  createdAt: string;
}

export interface CurrencyExchangeResponse {
  content: CurrencyExchange[];
  totalPages: number;
  totalElements: number;
  numberOfElements: number;
  number: number;
  size: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

interface CurrencyExchangeState {
  exchanges: CurrencyExchange[];
  loading: boolean;
  error: string | null;
  totalElements: number;
}

const initialState: CurrencyExchangeState = {
  exchanges: [],
  loading: false,
  error: null,
  totalElements: 0,
};

export const fetchCurrencyExchanges = createAsyncThunk('currencyExchange/fetchAll', async (params: any, thunkAPI) => {
  try {
    const result: CurrencyExchangeResponse = await currencyExchangeServices.fetchAllCurrencyExchangeRequests(params);
    return result;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error?.data || 'Failed to fetch');
  }
});

const currencyExchangeSlice = createSlice({
  name: 'currencyExchange',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchCurrencyExchanges.pending, state => {
      state.loading = true;
      state.error = null;
    });

    builder.addCase(fetchCurrencyExchanges.fulfilled, (state, action: PayloadAction<CurrencyExchangeResponse>) => {
      state.loading = false;
      state.exchanges = action.payload.content;
    });

    builder.addCase(fetchCurrencyExchanges.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

export const selectCurrencyExchanges = (state: RootState) => state.currencyExchange;

export default currencyExchangeSlice.reducer;
